import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/Card';
import { DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
export interface PageHeroProps {
  title: string;
  subtitle: string;
  /** Tailwind gradient classes, e.g. 'from-amber-500 via-orange-500 to-rose-500' */
  gradientClasses: string;
  djayEmotion: DjayEmotion;
  djayVariant: DjayVariant;
  /** Lucide icon shown as badge overlay on Djay */
  icon?: React.ElementType;
  /** Background class for the icon badge, e.g. 'bg-amber-400' */
  iconBgClass?: string;
  /** Floating emoji decorations — up to 2 */
  floatingEmojis?: [string, string?];
  /** Slot for stats pills or extra content inside the hero */
  children?: React.ReactNode;
}
export function PageHero({
  title,
  subtitle,
  gradientClasses,
  djayEmotion,
  djayVariant,
  icon: IconComponent,
  iconBgClass = 'bg-white',
  floatingEmojis,
  children
}: PageHeroProps) {
  return <Card className={`p-4 sm:p-8 bg-gradient-to-br ${gradientClasses} border-none shadow-xl text-white overflow-hidden relative`} data-id="element-2101">
      {/* Decorative background blurs */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-2102" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4" data-id="element-2103" />

      {/* Floating emoji decorations */}
      {floatingEmojis?.[0] && <motion.div className="hidden sm:block absolute top-4 right-8 text-4xl opacity-20" animate={{
      y: [0, -10, 0],
      rotate: [0, 10, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-2104">
          {floatingEmojis[0]}
        </motion.div>}
      {floatingEmojis?.[1] && <motion.div className="hidden sm:block absolute bottom-4 right-24 text-3xl opacity-20" animate={{
      y: [0, -8, 0],
      rotate: [0, -10, 0]
    }} transition={{
      duration: 3.5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    }} data-id="element-2105">
          {floatingEmojis[1]}
        </motion.div>}

      <div className="relative z-10 flex flex-row items-center gap-4 sm:gap-8" data-id="element-2106">
        {/* Djay with optional icon badge */}
        <div className="relative flex-shrink-0" data-id="element-2107">
          <motion.div animate={{
          y: [0, -5, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2108">
            {/* Mobile Djay */}
            <div className="block sm:hidden" data-id="element-2109">
              <DjayAvatar size="md" emotion={djayEmotion} variant={djayVariant} animate data-id="element-2110" />
            </div>
            {/* Desktop Djay */}
            <div className="hidden sm:block" data-id="element-2111">
              <DjayAvatar size="hero" emotion={djayEmotion} variant={djayVariant} animate data-id="element-2112" />
            </div>
          </motion.div>

          {/* Icon badge overlay */}
          {IconComponent && <motion.div className={`hidden sm:flex absolute -bottom-2 -right-2 ${iconBgClass} w-14 h-14 rounded-2xl items-center justify-center shadow-lg border-4 border-white/30`} animate={{
          rotate: [0, 5, -5, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2113">
              <IconComponent className="w-7 h-7 text-white" data-id="element-2114" />
            </motion.div>}
        </div>

        {/* Title, subtitle & children slot */}
        <div className="flex-1 text-left" data-id="element-2115">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black mb-1 sm:mb-2 tracking-tight" data-id="element-2116">
            {title}
          </h1>
          <p className="text-white/80 text-sm sm:text-lg font-medium mb-2 sm:mb-6 max-w-md" data-id="element-2117">
            {subtitle}
          </p>

          {/* Children slot — typically stats pills */}
          {children && <div className="flex flex-wrap justify-start gap-2 sm:gap-3" data-id="element-2118">
              {children}
            </div>}
        </div>
      </div>
    </Card>;
}
/** Reusable stats pill for inside PageHero */
export function HeroStatPill({
  icon: Icon,
  children
}: {
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-4 py-2 flex items-center gap-2" data-id="element-2119">
      <Icon className="w-5 h-5 text-white/70" data-id="element-2120" />
      <span className="font-bold text-sm" data-id="element-2121">{children}</span>
    </div>;
}